const API_URL = '/a/pbc-giftproducts/Prod/rules-settings/';

(function () {
  const GP = {
    rules: [],
    cart: {},
    popUpRule: null,
    popUpProductsInCart: 0,
    _fetchWrapped: false,
    _escHandler: null,

    async init() {
      await this.getRules();
      this.getPopUpRule(); // sets popUpRule & strips popup rule out of rules

      if (this.rules.length === 0 && !this.popUpRule) return;

      this.cart = await this.getCart();

      // initialize popup count from current cart (if any gifts from the popup rule already there)
      if (this.popUpRule) {
        this.popUpProductsInCart = this.countGiftsForRule(this.cart, this.popUpRule.SK);
        this.popUpProcess('render');
      }

      await this.validateRules();
    },

    async appProcess() {
      this.cart = await this.getCart();
      await this.validateRules();
    },

    createCustomEvents(eventName, data = {}) {
      document.dispatchEvent(new CustomEvent(eventName, { detail: data }));
    },

    currencyRate() {
      // Single, safe place for currency conversion
      const r = Number(
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1
      );
      return Number.isFinite(r) ? r : 1;
    },

    nonGiftItems(cart) {
      return (cart.items || []).filter(i => !i?.properties?.['__pbc_gift_product']);
    },

    giftItems(cart) {
      return (cart.items || []).filter(i => i?.properties?.['__pbc_gift_product']);
    },

    countGiftsForRule(cart, ruleId) {
      return this.giftItems(cart).filter(i => i?.properties?.['__pbc_gift_rule_id'] === ruleId).length;
    },

    buildCartView(cart) {
      const items = cart.items || [];
      const nonGifts = this.nonGiftItems(cart);
      const gifts = this.giftItems(cart);

      const totalNonGiftAmount = nonGifts.reduce((sum, i) => sum + i.price * i.quantity, 0);
      const totalNonGiftQty = nonGifts.reduce((sum, i) => sum + i.quantity, 0);
      const nonGiftVariantIdSet = new Set(nonGifts.map(i => i.id));

      // gifts keyed by rule id
      const giftsByRule = new Map();
      for (const g of gifts) {
        const r = g?.properties?.['__pbc_gift_rule_id'];
        if (!r) continue;
        if (!giftsByRule.has(r)) giftsByRule.set(r, []);
        giftsByRule.get(r).push(g);
      }

      return {
        items,
        nonGifts,
        gifts,
        giftsByRule,
        totalNonGiftAmount, // in cents
        totalNonGiftQty,
        nonGiftVariantIdSet
      };
    },

    async validateRules() {
      const toAdd = [];
      const toDelete = [];
      const rate = this.currencyRate();

      const cv = this.buildCartView(this.cart);

      const finalValidate = (rule) => {
        if (!rule || !Array.isArray(rule.products) || rule.products.length === 0) return;

        if (rule.actionType === 'showPopUp') {
          // Only open popup if we haven't reached the maximum number of products
          if (this.popUpProductsInCart < Number(rule.maxProductsToAdd || 0)) {
            this.popUpProcess('open');
          }
          return;
        }

        // prepare one gift per rule product (current logic)
        for (const p of rule.products) {
          if (!p?.variantId) continue;

          toAdd.push({
            id: p.variantId,
            quantity: 1,
            properties: {
              '__pbc_gift_product': 'true',
              '__pbc_gift_rule_id': rule.SK,
              '__pbc_enable_free_shipping': !!rule.freeShipping,
              '__pbc_gift_discount_type': rule?.discountType?.value ?? '',
              '__pbc_gift_discount_amount': Number(rule.discountAmount || 0) * rate
            }
          });
        }
      };

      const ensureRuleWindow = (rule, pass) => {
        // normalize “upper” logic for amount & count rules
        if (!rule?.condition) return false;
        const hasUpper = !!rule.condition.upperLimit;
        if (!hasUpper) return pass >= Number(rule.condition.min || 0);

        return (
          pass >= Number(rule.condition.min || 0) &&
          pass <= Number(rule.condition.max || Number.MAX_SAFE_INTEGER)
        );
      };

      const markGiftsToRemoveIfRuleBroken = (rule, isStillValid) => {
        const giftsForRule = cv.giftsByRule.get(rule.SK) || [];
        if (!giftsForRule.length) return;

        if (!isStillValid) {
          for (const g of giftsForRule) {
            toDelete.push({ id: g.key, quantity: 0 });
          }
        }
      };

      const validateBasedCartAmount = (rule) => {
        const minCents = Number(rule?.condition?.min || 0) * rate * 100;
        const maxCents = rule?.condition?.upperLimit
          ? Number(rule.condition.max || 0) * rate * 100
          : null;

        const pass = cv.totalNonGiftAmount; // cents
        const stillValid = maxCents == null
          ? pass >= minCents
          : pass >= minCents && pass <= maxCents;

        markGiftsToRemoveIfRuleBroken(rule, stillValid);
        if (stillValid) finalValidate(rule);
      };

      const validateBasedCartItemsCount = (rule) => {
        const pass = cv.totalNonGiftQty;
        const stillValid = ensureRuleWindow(rule, pass);

        markGiftsToRemoveIfRuleBroken(rule, stillValid);
        if (stillValid) finalValidate(rule);
      };

      const validateBasedSpecificProducts = (rule) => {
        const first = rule?.condition?.products?.[0];
        const wantedVariant = first && Number(first.variantId);
        if (!wantedVariant) {
          // malformed rule → remove existing gifts to be safe
          markGiftsToRemoveIfRuleBroken(rule, false);
          return;
        }

        const hasIt = cv.nonGiftVariantIdSet.has(wantedVariant);
        markGiftsToRemoveIfRuleBroken(rule, hasIt);
        if (hasIt) finalValidate(rule);
      };

      const validateBasedProductCombinations = (rule) => {
        const required = (rule?.condition?.products || [])
        .map(p => Number(p.variantId))
        .filter(Boolean);

        if (!required.length) {
          markGiftsToRemoveIfRuleBroken(rule, false);
          return;
        }

        // All required variants must be present among non-gift items
        const allPresent = required.every(vId => cv.nonGiftVariantIdSet.has(vId));
        markGiftsToRemoveIfRuleBroken(rule, allPresent);
        if (allPresent) finalValidate(rule);
      };

      // Validate standard rules
      for (const rule of this.rules) {
        switch (rule.ruleType) {
          case 'basedCartItemsCount':
            validateBasedCartItemsCount(rule);
            break;
          case 'basedCartAmount':
            validateBasedCartAmount(rule);
            break;
          case 'basedSpecificProducts':
            validateBasedSpecificProducts(rule);
            break;
          case 'basedProductCombinations':
            validateBasedProductCombinations(rule);
            break;
          default:
            // unknown rule → ignore
            break;
        }
      }

      // Validate popup rule after auto add — keeps UX consistent
      if (this.popUpRule) {
        switch (this.popUpRule.ruleType) {
          case 'basedCartItemsCount':
            validateBasedCartItemsCount(this.popUpRule);
            break;
          case 'basedCartAmount':
            validateBasedCartAmount(this.popUpRule);
            break;
          case 'basedSpecificProducts':
            validateBasedSpecificProducts(this.popUpRule);
            break;
          case 'basedProductCombinations':
            validateBasedProductCombinations(this.popUpRule);
            break;
        }
      }

      // Apply server changes
      if (toAdd.length > 0) {
        await this.addToCart(toAdd);
      }

      if (toDelete.length > 0) {
        await this.cartDeleteProducts(toDelete);
        this.createCustomEvents('pbc-gift-products:finish', await this.getCart());
        // keep old behavior; feel free to remove in theme drawers
        window.location.reload();
      } else {
        // fire finish event even if nothing deleted (useful for theme UIs)
        this.createCustomEvents('pbc-gift-products:finish', await this.getCart());
      }
    },

    // === Popup ===
    addToCartPopUp: async (event) => {
      event.preventDefault();
      const self = GP;

      if (!self.popUpRule) return;
      if (self.popUpProductsInCart >= Number(self.popUpRule.maxProductsToAdd || 0)) return;

      const form = event.currentTarget;
      const data = new FormData(form);

      const rate = self.currencyRate();

      data.append('properties[__pbc_gift_product]', 'true');
      data.append('properties[__pbc_gift_rule_id]', self.popUpRule.SK);
      data.append('properties[__pbc_enable_free_shipping]', !!self.popUpRule.freeShipping);
      data.append('properties[__pbc_gift_discount_type]', self.popUpRule?.discountType?.value ?? '');
      data.append('properties[__pbc_gift_discount_amount]', Number(self.popUpRule.discountAmount || 0) * rate);

      try {
        // optional: harmless if discount doesn't exist
        await fetch('/discount/PBC_FREE_SHIPPING').catch(() => {});

        const resp = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: new Headers({ 'PBC': 'true' }),
          body: data
        });

        // Don't assume 2xx; try to read JSON only if ok
        if (!resp.ok) {
          console.error('Popup add failed', await resp.text());
          return;
        }
        await resp.json();

        form.querySelector('.pbc-gift-products__popup-item-btn')?.classList.add('disabled');

        self.popUpProductsInCart += 1;

        if (self.popUpProductsInCart >= Number(self.popUpRule.maxProductsToAdd || 0)) {
          if (self.popUpRule.cartAction === 'cartRedirect') {
            self.popUpProcess('close');
            window.location.href = '/cart';
          } else {
            self.popUpProcess('close');
            window.location.reload();
          }
        }

        self.createCustomEvents('pbc-gift-products:finish', await self.getCart());
      } catch (er) {
        console.error('Error:', er);
      }
    },

    async addToCart(items) {
      if (!Array.isArray(items) || items.length === 0) return;

      // optional discount ping
      await fetch('/discount/PBC_FREE_SHIPPING').catch(() => {});

      const resp = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'PBC': 'true'
        }),
        body: JSON.stringify({ items })
      });

      if (!resp.ok) {
        console.error('addToCart failed', await resp.text());
        return;
      }

      await resp.json();
    },

    async cartDeleteProducts(items) {
      // Shopify cart/change.js with key works; keep sequential to preserve server state
      for (const item of items) {
        try {
          const resp = await fetch((window.Shopify?.routes?.root || '/') + 'cart/change.js', {
            method: 'POST',
            headers: new Headers({
              'Content-Type': 'application/json',
              'PBC': 'true'
            }),
            body: JSON.stringify(item)
          });
          if (!resp.ok) {
            console.error('change.js failed', await resp.text());
          }
        } catch (e) {
          console.error(e);
        }
      }
    },

    async getCart() {
      try {
        const resp = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
          method: 'GET',
          headers: new Headers({ 'PBC': 'true' })
        });
        if (!resp.ok) {
          console.error('cart.js failed', await resp.text());
          return { items: [] };
        }
        return await resp.json();
      } catch (e) {
        console.error('Error:', e);
        return { items: [] };
      }
    },

    async getRules() {
      try {
        const resp = await fetch(`${API_URL}?url=${encodeURIComponent(window.location.href)}`);
        const data = await resp.json();
        this.rules = Array.isArray(data?.data) ? data.data : [];
        return this.rules;
      } catch (e) {
        console.log(e);
        this.rules = [];
        return this.rules;
      }
    },

    getPopUpRule() {
      if (!Array.isArray(this.rules) || this.rules.length === 0) return;

      for (const r of this.rules) {
        if (r.actionType === 'showPopUp') {
          this.popUpRule = r;
          this.appendStyleVariables(r);
          this.createRulesWithoutPopUp(this.rules);
          return;
        }
      }
    },

    createRulesWithoutPopUp(rules) {
      this.rules = (rules || []).filter(r => r.actionType !== 'showPopUp');
    },

    popUpProcess(trigger) {
      const rule = this.popUpRule;
      if (!rule) return;

      const POPUP_CLASS = 'pbc-gift-products';
      const BTN_CLOSE_CLASS = 'pbc-gift-products__popup-close';

      function onEsc(e) {
        if (e.key === 'Escape') closePopUp();
      }

      function openPopUp() {
        const el = document.querySelector(`.${POPUP_CLASS}`);
        if (!el) return;
        el.classList.add('active');

        // attach ESC once
        if (!GP._escHandler) {
          GP._escHandler = onEsc;
          document.addEventListener('keydown', GP._escHandler);
        }
      }

      function closePopUp() {
        const el = document.querySelector(`.${POPUP_CLASS}`);
        if (el) {
          el.classList.remove('active');
          el.querySelectorAll('.pbc-gift-products__popup-item-btn').forEach(b => b.classList.remove('disabled'));
        }
        if (GP._escHandler) {
          document.removeEventListener('keydown', GP._escHandler);
          GP._escHandler = null;
        }
      }

      function popUpListeners(root) {
        const btnClose = root.querySelector(`.${BTN_CLOSE_CLASS}`);
        const productForms = root.querySelectorAll('.pbc-gift-products__popup-item-product-form');

        btnClose?.addEventListener('click', closePopUp);
        productForms.forEach(f => f.addEventListener('submit', GP.addToCartPopUp));
      }

      function renderPopUp() {
        if (document.querySelector(`.${POPUP_CLASS}`)) return; // already rendered

        const productsHtml = (rule.products || []).map(p => {
          const title = p?.variantTitle !== '' ? p?.variantTitle : p?.title || '';
          const id = p?.variantId || '';
          const img = p?.image || '';

          return `
            <li class="pbc-gift-products__popup-item">
              <div class="pbc-gift-products__popup-item-image-box">
                <img src="${img}" alt="${title}" class="pbc-gift-products__popup-item-image" width="100%">
              </div>
              <h3 class="pbc-gift-products__popup-item-title">${title}</h3>
              <form method="post" action="/cart/add" accept-charset="UTF-8"
                    class="form pbc-gift-products__popup-item-product-form"
                    enctype="multipart/form-data" novalidate data-type="add-to-cart-form">
                <input type="hidden" name="id" value="${id}">
                <input type="hidden" name="quantity" min="1" value="1">
                <button type="submit" class="pbc-gift-products__popup-item-btn">
                  ${rule.popUpBtnLabel || 'Add'}
                </button>
              </form>
            </li>`;
        }).join('');

        const html = `
          <div class="pbc-gift-products">
            <div class="pbc-gift-products__backdrop">
              <div class="pbc-gift-products__popup">
                <button class="pbc-gift-products__popup-close" aria-label="Close popup">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50" height="50" aria-hidden="true">
                    <path d="M9.156 6.312 6.313 9.156 22.156 25 6.219 40.969 9.031 43.781 25 27.844 40.938 43.781 43.781 40.938 27.844 25 43.688 9.156 40.844 6.312 25 22.156Z"/>
                  </svg>
                </button>
                <div class="pbc-gift-products__popup-wrapper">
                  <h2 class="pbc-gift-products__popup-title">${rule.popUpTitle || ''}</h2>
                  <p class="pbc-gift-products__popup-subtitle">${rule.popUpSubtitle || ''}</p>
                  <ul class="pbc-gift-products__popup-list">
                    ${productsHtml}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        `;
        document.body.insertAdjacentHTML('beforeend', html);
        const root = document.querySelector(`.${POPUP_CLASS}`);
        if (root) popUpListeners(root);
      }

      if (trigger === 'open') {
        openPopUp();
        return;
      }
      if (trigger === 'close') {
        closePopUp();
        return;
      }
      if (trigger === 'render') {
        renderPopUp();
        return;
      }
    },

    appendStyleVariables(rule) {
      if (!rule) return;

      // only inject once
      if (!document.getElementById('pbcCssVariables')) {
        const css = [
          ':root{',
          `--gift-popUpTitleFontSize:${Number(rule.popUpTitleFontSize || 0)}px;`,
          `--gift-popUpTitleTextColor:${rule.popUpTitleTextColor || 'inherit'};`,
          `--gift-popUpSubTitleFontSize:${Number(rule.popUpSubTitleFontSize || 0)}px;`,
          `--gift-popUpSubTitleTextColor:${rule.popUpSubTitleTextColor || 'inherit'};`,
          `--gift-popUpBtnTextColor:${rule.popUpBtnTextColor || '#000'};`,
          `--gift-popUpBtnBackgroundColor:${rule.popUpBtnBackgroundColor || 'transparent'};`,
          `--gift-popUpBtnBorderColor:${rule.popUpBtnBorderColor || 'currentColor'};`,
          `--gift-popUpBtnBorderRadius:${Number(rule.popUpBtnBorderRadius || 0)}px;`,
          '}'
        ].join('\n');

        const style = document.createElement('style');
        style.id = 'pbcCssVariables';
        style.textContent = css;
        document.head.appendChild(style);
      }

      // global stylesheet link – also only once
      if (!document.getElementById('pbcGiftGlobalCss')) {
        const link = document.createElement('link');
        link.id = 'pbcGiftGlobalCss';
        link.rel = 'stylesheet';

        let href = 'https://giftproducts.polarbearapps.com/frontend/style.css';
        const env = (typeof process !== 'undefined' && process?.env?.ENVIRONMENT) || undefined;
        if (env === 'dev') href = 'https://giftproducts/frontend/style.css';
        else if (env === 'stage') href = 'https://giftproducts-staging.polarbearapps.com/frontend/style.css';

        link.href = href;
        document.getElementById('pbcCssVariables')?.after(link);
      }
    },

    setupFetchListener() {
      if (this._fetchWrapped) return;

      const originalFetch = window.fetch.bind(window);
      const self = this;

      function hasPbcHeader(init) {
        if (!init || !init.headers) return false;

        // normalize for Headers / object
        if (init.headers instanceof Headers) {
          return init.headers.has('PBC');
        }
        const entries = init.headers;
        // case-insensitive lookup
        for (const k of Object.keys(entries)) {
          if (k.toLowerCase() === 'pbc') return true;
        }
        return false;
      }

      window.fetch = async function wrappedFetch(input, init) {
        const response = await originalFetch(input, init);

        // derive URL as string
        const url = (typeof input === 'string') ? input : (input?.url || '');

        const isCartMutation =
          typeof url === 'string' &&
          (url.includes('/cart/add') ||
            url.includes('/cart/change') ||
            url.includes('/cart/update') ||
            url.includes('/cart/clear'));

        // Avoid recursion & only react to external (non-PBC) mutations
        if (isCartMutation && !hasPbcHeader(init)) {
          await self.appProcess();
        }

        return response;
      };

      this._fetchWrapped = true;
    }
  };

  // expose (back-compat with your global)
  window.pbcGiftProductsProcess = GP;

  // boot
  GP.init();
  GP.setupFetchListener();
})();
